/**
 * Copy del hub, transcrito de `DEMO_0_Hub.png` sección a sección.
 *
 * REGLAS QUE GOBIERNAN ESTE FICHERO:
 *  · Los DATOS de la gira (ciudades, fechas, programa, estado de venta) NO viven
 *    aquí: salen de `src/features/events` (ADR-003). Aquí solo está el copy
 *    editorial del hub, que no es un dato de negocio.
 *  · Acentuación española correcta aunque el mockup la pierda (ADR-003 §5).
 *  · La marca es AΩGR, nunca "AOGR" (ADR-002). El rótulo lo pinta `Brandmark`.
 */
import type { OrnamentIconName } from '@/components/ornaments';
import type { FaqItem } from '@/features/events';

export const HUB_META = {
  documentTitle: 'Gira 2027 · Nueva Humanidad Galáctica',
  description:
    'La gira 2027 de Azrahel del Mayor: cinco destinos para desprogramar la percepción, activar tu campo cuántico y recordar tu origen galáctico. Funes, Buenos Aires, Bogotá, Ciudad de México y Cancún.',
} as const;

export const HERO = {
  eyebrow: 'Gira 2027',
  /** Tres líneas, la tercera en oro. El mockup las pone en VERSALES. */
  lines: ['Nueva', 'Humanidad', 'Galáctica'] as const,
  subtitle: 'Un viaje de transformación global y cósmica',
  ctaPrimary: 'Descubrir destinos',
  ctaSecondary: 'Conocer la experiencia',
  portraitAlt:
    'Azrahel del Mayor, de camisa blanca, mirando hacia la luz sobre una bahía al amanecer',
} as const;

export const MANTRA = 'Mírate. Libérate del sueño. Despierta.';

/**
 * MANIFIESTO — el bloque 2 del hub (master prompt §7 y
 * `docs/brief/07_ARQUITECTURA_LANDINGS.md` §5.2: *"Qué es la gira: el
 * manifiesto (despertar, semillas estelares, 2040) — la promesa global"*).
 * ADICIÓN EXIGIDA POR EL BRIEF (ADR-006 §3), registrada en `VISUAL_DEVIATIONS`.
 *
 * NI UNA LÍNEA ES REDACCIÓN MÍA: cada una es vocabulario del propio cliente, y
 * aquí queda dicho de dónde sale, porque un manifiesto es justo el sitio donde
 * es más fácil colar una frase inventada y más difícil que se note.
 *
 *  · "No es una conferencia. Es un viaje de despertar."
 *      → `00_START_HERE_PROMPT_MAESTRO_CLAUDE.md` §3: *"La idea esencial no es
 *        'una conferencia espiritual'. Es un viaje de despertar."*
 *  · "semillas estelares" y "los 1.000 sueños del yo perdido"
 *      → `05_QUE_ES_ESTO_DE_VERDAD.md` §2 (la cosmología, literal).
 *  · "recuperar soberanía" y "activar los sentidos internos"
 *      → `00_START_HERE_PROMPT_MAESTRO_CLAUDE.md` §3, misma frase.
 *  · "el reloj corre hacia el 2040"
 *      → `02_BRIEFING_ESTRATEGIA.md` §2, gancho escrito por el cliente.
 *  · "Nueva Humanidad Galáctica" → el nombre de la propia gira.
 *
 * El brief pide **"copy breve, con ritmo; no un muro de texto"**: de ahí tres
 * tiempos cortos y un cierre, y no un párrafo largo.
 *
 * SIN DATOS DE NEGOCIO (ADR-003): aquí no hay precio, aforo, sede, cifra de
 * asistentes ni testimonio. Y sin promesa médica (§5): se habla de despertar y
 * de conciencia, nunca de curar.
 */
export interface ManifestoBeat {
  readonly id: string;
  readonly icon: OrnamentIconName;
  readonly title: string;
  readonly text: string;
}

export const MANIFESTO = {
  eyebrow: 'Qué es la gira',
  titleLines: ['No es una conferencia.', 'Es un viaje de despertar.'] as const,
  beats: [
    {
      id: 'el-sueno',
      icon: 'moon',
      title: 'El sueño',
      text: 'Somos semillas estelares dormidas en los 1.000 sueños del yo perdido, atrapadas en los bucles de la Matrix.',
    },
    {
      id: 'la-soberania',
      icon: 'eye',
      title: 'La soberanía',
      text: 'Despertar es salir de ese sueño: recuperar la soberanía sobre uno mismo y volver a activar los sentidos internos.',
    },
    {
      id: 'el-2040',
      icon: 'sun',
      title: 'El 2040',
      text: 'Y hacerlo ahora, porque el reloj corre hacia el 2040: el salto que da nombre a toda esta transformación.',
    },
  ] satisfies readonly ManifestoBeat[],
  closing: 'Ese es el viaje: de los 1.000 sueños a la Nueva Humanidad Galáctica.',
} as const;

export const ROUTE = {
  title: 'La Ruta del Despertar',
  /* Sin bajada: el mockup no la tiene y ADR-005 §1.1 no admite bloques de mas. */
} as const;

export interface ChamberFeature {
  readonly id: string;
  readonly icon: OrnamentIconName;
  readonly title: string;
}

export const CHAMBER = {
  /** Dos líneas en el mockup; se conservan como salto controlado. */
  titleLines: ['La Cámara', 'Cuántica Pleyadiana'] as const,
  paragraph:
    'Un espacio de alta frecuencia donde la ciencia, la conciencia y la luz se unen para activar tu campo cuántico, rearmonizar tu ser y expandir tu propósito.',
  imageAlt:
    'Interior luminoso de la Cámara Cuántica Pleyadiana, con un portal de geometría sagrada brillando en el centro',
  features: [
    { id: 'activacion', icon: 'seed', title: 'Activación cuántica' },
    { id: 'rearmonizacion', icon: 'infinity', title: 'Rearmonización integral' },
    { id: 'expansion', icon: 'star', title: 'Expansión de conciencia' },
    { id: 'alineacion', icon: 'spiral', title: 'Alineación galáctica' },
  ] satisfies readonly ChamberFeature[],
} as const;

/**
 * FAQ DEL HUB. Va aquí y no en `src/features/events` a propósito: el hub vende
 * **la gira entera**, no una sede, así que esto no es dato de evento (y
 * `features/events` es dominio de FORGE-ALPHA). Enfoque del master prompt §7.
 *
 * REGLA DURA (ADR-003 §2): lo que todavía no existe **no se responde con un dato
 * inventado**. Precio, sede y fecha de apertura de venta se responden diciendo
 * que se anunciarán, y la pregunta se marca `isPending` para que se vea.
 *
 * MISMO TIPO QUE LAS CINCO SEDES (ADR-006). La FAQ del hub es contenido de
 * página y la de cada ciudad es dato de evento, pero la FORMA es la misma:
 * `FaqItem`, y la pinta el mismo `FaqSection` de `pages/shared/BriefBlocks`.
 * El `satisfies` no es adorno: sin él, un `isPending` mal escrito o una
 * pregunta sin `id` no explota aquí sino en el punto de uso, y el error señala
 * al componente en vez de al dato que está mal.
 */
export const HUB_FAQ = [
  {
    id: 'hub-faq-que-es',
    question: '¿En qué consiste la gira y por qué cinco ciudades?',
    answer:
      'Nueva Humanidad Galáctica es una gira de cinco encuentros presenciales por Latinoamérica. La estructura es la misma en todas las sedes —dos días de inmersión— pero cada ciudad tiene su propio giro: el contenido se adapta a la sensibilidad de cada público, del enfoque analítico de Buenos Aires al ancestral de Ciudad de México.',
    isPending: false,
  },
  {
    id: 'hub-faq-cancun',
    question: '¿Qué diferencia hay entre una conferencia y la convivencia de Cancún?',
    answer:
      'Las tres conferencias (Buenos Aires, Bogotá y Ciudad de México) son dos días de trabajo en sala, sábado y domingo. Cancún / Riviera Maya no es una conferencia: es una convivencia en petit comité, con acceso directo a Azrahel, meditación al amanecer, cenote, Cámara Cuántica al atardecer y fogata ritual en la playa.',
    isPending: false,
  },
  {
    id: 'hub-faq-camara',
    question: '¿Qué es la Cámara Cuántica Pleyadiana?',
    answer:
      'Es la experiencia central de cada encuentro: una sesión grupal en vivo pensada para activar tu campo cuántico, rearmonizar tu ser y expandir tu propósito. Es una experiencia de desarrollo personal y espiritual; no es un tratamiento médico ni psicológico.',
    isPending: false,
  },
  {
    id: 'hub-faq-idioma',
    question: '¿En qué idioma son los encuentros?',
    answer: 'En español, en todas las sedes.',
    isPending: false,
  },
  {
    id: 'hub-faq-llevar',
    question: '¿Hay que llevar algo?',
    answer:
      'Nada imprescindible más allá de ropa cómoda y ganas de trabajar contigo. Si algún encuentro requiere material concreto, se indicará por correo a quienes tengan su entrada antes de la fecha.',
    isPending: false,
  },
  {
    id: 'hub-faq-accesibilidad',
    question: '¿Los espacios son accesibles?',
    answer:
      'Las sedes definitivas aún no están cerradas. Cuando se anuncien, se publicarán aquí sus condiciones de accesibilidad. Si necesitas una adaptación concreta, escríbenos y lo resolvemos contigo antes de que compres tu entrada.',
    isPending: true,
  },
  {
    id: 'hub-faq-entradas',
    question: '¿Cómo y cuándo se compran las entradas?',
    answer:
      'La venta todavía no está abierta. Los tipos de entrada y sus precios se anunciarán en la página de cada destino en cuanto se confirmen, y la compra se hará en línea desde esta misma web.',
    isPending: true,
  },
  {
    id: 'hub-faq-cambios',
    question: '¿Puedo cambiar mi entrada de ciudad o de fecha?',
    answer:
      'La política de cambios y devoluciones se publicará junto con la apertura de la venta, antes de que se pueda comprar ninguna entrada.',
    isPending: true,
  },
  {
    id: 'hub-faq-contacto',
    question: '¿Cómo contacto con la organización?',
    answer:
      'A través de los canales de la Escuela Pleyadiana AΩGR. Cuando se abra la venta se publicará aquí una dirección de contacto directa para dudas sobre entradas y accesibilidad.',
    isPending: true,
  },
] as const satisfies readonly FaqItem[];

export const AZRAHEL = {
  nameLead: 'Azrahel',
  nameTail: 'del Mayor',
  /**
   * Bio del mockup. Corregido "AOGR" -> AΩGR (ADR-002) y restituidos los acentos
   * que el generador de imagen perdió ("guía", "misión", "galáctico").
   */
  paragraphs: [
    'Maestro y guía espiritual, canal de enseñanzas pleyadianas y fundador de AΩGR – Escuela Pleyadiana.',
    'Su misión es acompañar a la humanidad en el recuerdo de su origen galáctico y en la creación de una nueva realidad basada en el amor, la unidad y la conciencia.',
  ] as const,
  portraitAlt: 'Retrato de Azrahel del Mayor sobre un cielo luminoso',
} as const;
